// services/transactionsService.ts

import { BUSINESS_TYPE_ENUM } from "../helpers/HelperConstants";
import { Transaction } from "../helpers/HelperInterfaces";
import config from "../config";

export class TransactionFilters {
  businessName: string   = '';
  businessTypes: string[]= [];
  dateFrom: string       = '';
  dateTo: string         = '';
  showExpense: boolean   = true;
  showIncome: boolean    = true;
  maxResults: number     = 50;

  constructor(maxResults: number = 50, isDraft: boolean = false) {
    this.maxResults = maxResults

    if (isDraft == true) {
      this.showExpense = false;
      this.showIncome = false;
    }
  }
}

const BASE_URL = config.backendUrl;

export interface CreateTransactionPayload {
  businessName: string;
  businessType: string;
  description: string;
  accountId: string;
  shouldUpdateAccount: boolean;
  amount: number;
  isExpense: boolean;
  date: string; // ISO date string
}

interface FetchTransactionsResponse {
  transactions: Transaction[];
  count: number;
}

export async function fetchTransactions(
  userId: number, 
  page: number,
  filters?: TransactionFilters
): Promise<[Transaction[], number]> {

  const params = new URLSearchParams();
  
  if (filters?.businessName.trim()) {
    params.append('businessName', filters.businessName.trim());
  }
  if (filters?.businessTypes.length) {
    filters.businessTypes.forEach((t) => params.append('businessType', BUSINESS_TYPE_ENUM[t]));
  }
  if (filters?.dateFrom) {
    params.append('dateFrom', filters.dateFrom);
  }
  if (filters?.dateTo) {
    params.append('dateTo', filters.dateTo);
  }
  if (filters?.maxResults) {
    params.append('maxResults', `${filters.maxResults}`);
  }
  params.append('showExpense', filters?.showExpense ? "true" : "false");
  params.append('showIncome', filters?.showIncome ? "true" : "false");
  const query = params.toString()

  const response = await fetch(`${BASE_URL}/user/${userId}/transactions?page=${page}&${query ? `${query}` : ''}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch transactions: ${response.statusText}`);
  }
  return response.json()
  .then((rawData) => {
    const data = rawData as FetchTransactionsResponse;

    const transactions: Transaction[] = data.transactions.map((tx : any): Transaction => {
      return {
        businessId: tx.businessId,
        businessName: tx.businessName,
        businessType: tx.businessType,
        description: tx.description,
        accountId: tx.accountId,
        amount: tx.amount,
        isExpense: (tx.isExpense === "True") ? true : false,
        createdDate: tx.createdDate
      }
    })

    const count = data.count || 0;
    return [transactions, count]
  });
}

/** POST /user/:id/transaction - create a new transaction */
export async function createTransaction(
  userId: number,
  payload: CreateTransactionPayload
): Promise<[Promise<Transaction> | undefined, string]> {
  const response = await fetch(`${BASE_URL}/user/${userId}/transactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await response.json();
  if (!response.ok) {
    let errorText: string = json.statusText;
    return [undefined, errorText.substring(6)]
  }
  return [json,""]
}

// Returns last 10 transactions
export function filterRecentTransactions(transactions: Transaction[]): Transaction[] {
  const sorted = [...transactions].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 5000);

  const pastWeek = sorted.filter(
    (tx) => new Date(tx.createdDate) >= oneWeekAgo
  );

  // Return whichever is the smaller set
  return pastWeek.length <= 10 ? pastWeek : sorted.slice(0, 10);
}